import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Ticket, Copy, X, Download, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES, formatDuration, formatBandwidth } from "@/lib/format";

const NO_PACKAGE = "__none__";
const BATCH_PAGE_SIZE = 20;
const SEARCH_PAGE_SIZE = 20;
const DRILL_PAGE_SIZE = 20;

const STATUS_STYLE = {
  unused: "text-nhp-primary", redeemed: "text-nhp-info", revoked: "text-nhp-critical", expired: "text-nhp-muted",
};

export default function Vouchers() {
  const [batches, setBatches] = useState([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", package_id: "", quantity: 10, duration_minutes: "",
    download_kbps: "", upload_kbps: "", simultaneous_devices: "",
  });
  const [newCodes, setNewCodes] = useState(null); // { batch, codes } after create
  const [drillBatch, setDrillBatch] = useState(null);
  const [drillCodes, setDrillCodes] = useState([]);
  const [drillPage, setDrillPage] = useState(1);
  const [drillTotal, setDrillTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchResults, setSearchResults] = useState(null); // null = not searched yet
  const [searching, setSearching] = useState(false);

  // Memoize the active-package list — recomputes only when `packages` changes,
  // instead of on every render of the parent Vouchers component.
  const activePackages = useMemo(
    () => packages.filter((p) => p.active),
    [packages],
  );

  const load = async (p = batchPage) => {
    try {
      const [b, pkgs] = await Promise.all([
        api.get("/admin/vouchers/batches", { params: { page: p, page_size: BATCH_PAGE_SIZE } }),
        api.get("/admin/packages", { params: { page_size: 1000 } }), // full list for the package picker
      ]);
      setBatches(b.data.items);
      setBatchTotal(b.data.total);
      setPackages(pkgs.data.items);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(batchPage); }, [batchPage]);

  const standalone = form.package_id === NO_PACKAGE;

  const create = async () => {
    if (!form.name || !form.package_id || !form.quantity) {
      toast.error("Fill in every field");
      return;
    }
    if (standalone && (!form.duration_minutes || !form.download_kbps || !form.upload_kbps)) {
      toast.error("Duration, download, and upload speed are required for a package-less voucher");
      return;
    }
    try {
      const { data } = await api.post("/admin/vouchers/batches", {
        name: form.name, package_id: standalone ? null : form.package_id, quantity: Number(form.quantity),
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : undefined,
        download_kbps: standalone ? Math.round(Number(form.download_kbps) * 1024) : undefined,
        upload_kbps: standalone ? Math.round(Number(form.upload_kbps) * 1024) : undefined,
        simultaneous_devices: standalone && form.simultaneous_devices ? Number(form.simultaneous_devices) : undefined,
      });
      setNewCodes(data);
      setOpen(false);
      setForm({ name: "", package_id: "", quantity: 10, duration_minutes: "", download_kbps: "", upload_kbps: "", simultaneous_devices: "" });
      setBatchPage(1);
      await load(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { setSearchPage(1); }, [search]);

  useEffect(() => {
    if (!search.trim()) { setSearchResults(null); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/vouchers/search", {
          params: { q: search.trim(), page: searchPage, page_size: SEARCH_PAGE_SIZE },
        });
        setSearchResults(data.items);
        setSearchTotal(data.total);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, searchPage]);

  const openBatch = async (batch, p = 1) => {
    try {
      setDrillBatch(batch);
      setDrillPage(p);
      const { data } = await api.get(`/admin/vouchers/batches/${batch.id}/codes`, {
        params: { page: p, page_size: DRILL_PAGE_SIZE },
      });
      setDrillCodes(data.items);
      setDrillTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this voucher? Users can no longer redeem it.")) return;
    try {
      await api.post(`/admin/vouchers/${id}/revoke`);
      toast.success("Revoked");
      openBatch(drillBatch, drillPage);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadCsv = (codes, batchName) => {
    const rows = ["code,status"].concat(codes.map((c) => `${c.code || c},${c.status || "unused"}`));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vouchers-${batchName.replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // The on-screen drill-in view is paginated, but a CSV export should always
  // contain every code in the batch -- fetch the full set (capped at the
  // batch's own 1000-code max) just for this, rather than reusing drillCodes.
  const downloadBatchCsv = async (batch) => {
    try {
      const { data } = await api.get(`/admin/vouchers/batches/${batch.id}/codes`, {
        params: { page: 1, page_size: 1000 },
      });
      downloadCsv(data.items, batch.name);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        testId="vouchers-header"
        title="Voucher Batches"
        subtitle="Bulk-generate offline access codes for schools, cybercafés, promos"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-batch-btn"
                className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
              >
                <Plus className="h-4 w-4 mr-1" /> NEW BATCH
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">New Voucher Batch</DialogTitle>
                <DialogDescription className="text-nhp-muted">Generate N codes redeemable for the same package.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Batch name</Label>
                  <Input data-testid="batch-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" placeholder="Nov 2026 school promo" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Package</Label>
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger data-testid="batch-package" className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10">
                      <SelectValue placeholder="Pick a package…" />
                    </SelectTrigger>
                    <SelectContent className="bg-nhp-surface border-nhp-border text-nhp-text">
                      <SelectItem value={NO_PACKAGE}>No package — set duration &amp; speed manually</SelectItem>
                      {activePackages.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name} — {KES(p.price_kes)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Quantity (1-1000)</Label>
                  <Input data-testid="batch-quantity" type="number" value={form.quantity} min={1} max={1000} onChange={(e) => setForm({ ...form, quantity: +e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                {standalone ? (
                  <>
                    <div>
                      <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Duration (minutes)</Label>
                      <Input data-testid="batch-duration" type="number" value={form.duration_minutes} min={1} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" placeholder="e.g. 120" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Download (Mbps)</Label>
                        <Input data-testid="batch-download" type="number" step="0.1" min={0} value={form.download_kbps} onChange={(e) => setForm({ ...form, download_kbps: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Upload (Mbps)</Label>
                        <Input data-testid="batch-upload" type="number" step="0.1" min={0} value={form.upload_kbps} onChange={(e) => setForm({ ...form, upload_kbps: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Max simultaneous devices</Label>
                      <Input data-testid="batch-devices" type="number" min={1} value={form.simultaneous_devices} onChange={(e) => setForm({ ...form, simultaneous_devices: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" placeholder="1" />
                    </div>
                    <p className="text-xs text-nhp-muted leading-relaxed">Not tied to any package — this batch defines its own duration, speed, and device limit.</p>
                  </>
                ) : (
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Duration override (minutes, optional)</Label>
                    <Input data-testid="batch-duration" type="number" value={form.duration_minutes} min={1} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" placeholder="Leave blank to use the package's own duration" />
                    <p className="mt-1 text-xs text-nhp-muted">Bandwidth and price still come from the package — this only changes how long the code stays active once redeemed.</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button data-testid="batch-save-btn" onClick={create} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
                  GENERATE
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-nhp-muted" />
          <Input
            data-testid="voucher-search"
            placeholder="Search by voucher, phone, or MAC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-nhp-surface border-nhp-border text-nhp-text rounded-none h-10 font-mono"
          />
        </div>

        {searchResults !== null && (
          <div className="border border-nhp-border bg-nhp-surface">
            <div className="px-4 md:px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
              {searching ? "Searching…" : `${searchTotal} match${searchTotal === 1 ? "" : "es"}`}
            </div>
            <div className="divide-y divide-nhp-border">
              {searchResults.length === 0 && !searching && (
                <div className="p-8 text-center text-sm text-nhp-muted">No vouchers found.</div>
              )}
              {searchResults.map((v) => (
                <div key={v.id} data-testid={`voucher-search-row-${v.id}`} className="px-4 md:px-6 py-3 flex flex-wrap items-center justify-between gap-2 font-mono text-sm">
                  <div className="flex flex-wrap items-center gap-2 md:gap-3 min-w-0">
                    <Ticket className="h-3.5 w-3.5 text-nhp-muted shrink-0" />
                    <span className={STATUS_STYLE[v.status] || ""}>{v.code}</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted">{v.status}</span>
                    <span className="text-xs text-nhp-muted">{v.batch_name} · {v.package_name || "Custom"}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nhp-muted">
                    {v.redeemed_phone && <span>→ {v.redeemed_phone}</span>}
                    {v.redeemed_mac && <span>{v.redeemed_mac}</span>}
                    {v.redeemed_at && <span>{new Date(v.redeemed_at).toLocaleString()}</span>}
                  </div>
                </div>
              ))}
            </div>
            <Pager page={searchPage} total={searchTotal} pageSize={SEARCH_PAGE_SIZE} itemLabel="matches" onChange={setSearchPage} testId="voucher-search" />
          </div>
        )}

        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-3">Batch</div>
            <div className="col-span-3">Package</div>
            <div className="col-span-2">Unused / Total</div>
            <div className="col-span-2">Redeemed</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {batches.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No voucher batches yet.</div>}
          <div className="divide-y divide-nhp-border">
            {batches.map((b) => (
              <div key={b.id} data-testid={`batch-row-${b.id}`} className="flex flex-col gap-2 md:grid md:grid-cols-12 px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg">
                <div className="md:col-span-3">
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-nhp-muted font-mono">#{b.id} · {new Date(b.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm md:contents">
                  <div className="md:col-span-3">
                    {b.package_id ? (
                      <>
                        <div className="font-medium">{b.package_name}</div>
                        <div className="text-xs text-nhp-muted">{KES(b.package_price_kes)}</div>
                      </>
                    ) : (
                      <>
                        <div className="font-medium">Custom (no package)</div>
                        <div className="text-xs text-nhp-muted">
                          {formatDuration(b.duration_minutes)} · ↓{formatBandwidth(b.download_kbps)} ↑{formatBandwidth(b.upload_kbps)}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="font-mono md:col-span-2">
                    <span className="text-nhp-primary">{b.unused_count}</span>
                    <span className="text-nhp-muted"> / {b.quantity} unused</span>
                  </div>
                  <div className="font-mono text-nhp-muted md:col-span-2">{b.redeemed_count} used</div>
                </div>
                <div className="flex justify-end gap-2 md:col-span-2">
                  <Button variant="ghost" size="sm" onClick={() => openBatch(b)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-text" data-testid={`view-batch-${b.id}`}>
                    View codes <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <Pager page={batchPage} total={batchTotal} pageSize={BATCH_PAGE_SIZE} itemLabel="batches" onChange={setBatchPage} testId="batches" />
        </div>
      </div>

      {/* Codes just generated */}
      <Dialog open={!!newCodes} onOpenChange={(v) => !v && setNewCodes(null)}>
        <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Batch created — {newCodes?.batch?.name}</DialogTitle>
            <DialogDescription className="text-nhp-muted">
              Copy or download now. Codes are only shown here once, but you can revisit them from the batch detail.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto border border-nhp-border bg-nhp-bg p-3 font-mono text-sm grid grid-cols-2 sm:grid-cols-3 gap-2">
            {newCodes?.codes?.map((c) => (
              <div key={c} data-testid={`new-code-${c}`} className="px-2 py-1 border border-nhp-border text-nhp-primary tabular-nums">
                {c}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(newCodes.codes.join("\n"));
                toast.success("Copied to clipboard");
              }}
              variant="ghost" className="rounded-none"
            ><Copy className="h-4 w-4 mr-2" /> Copy all</Button>
            <Button
              onClick={() => downloadCsv(newCodes.codes, newCodes.batch.name)}
              className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
              data-testid="download-csv-btn"
            ><Download className="h-4 w-4 mr-2" /> Download CSV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-in existing codes */}
      <Dialog open={!!drillBatch} onOpenChange={(v) => !v && setDrillBatch(null)}>
        <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{drillBatch?.name}</DialogTitle>
            <DialogDescription className="text-nhp-muted">
              {drillBatch?.package_id ? drillBatch.package_name : "Custom (no package)"} · {drillTotal} codes
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto border border-nhp-border bg-nhp-bg divide-y divide-nhp-border">
            {drillCodes.map((c) => (
              <div key={c.id} data-testid={`code-row-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 font-mono text-sm">
                <div className="flex flex-wrap items-center gap-2 md:gap-3 min-w-0">
                  <Ticket className="h-3.5 w-3.5 text-nhp-muted shrink-0" />
                  <span className={c.status === "unused" ? "text-nhp-primary" : "text-nhp-muted"}>{c.code}</span>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-nhp-muted">{c.status}</span>
                  {c.duration_minutes && <span className="text-xs text-nhp-muted">{formatDuration(c.duration_minutes)}</span>}
                  {c.redeemed_phone && <span className="text-xs text-nhp-muted">→ {c.redeemed_phone}</span>}
                  {c.redeemed_mac && <span className="text-xs text-nhp-muted">{c.redeemed_mac}</span>}
                  {c.redeemed_at && <span className="text-xs text-nhp-muted">{new Date(c.redeemed_at).toLocaleString()}</span>}
                </div>
                {c.status === "unused" && (
                  <Button
                    variant="ghost" size="sm" onClick={() => revoke(c.id)}
                    className="h-7 rounded-none text-nhp-muted hover:text-nhp-critical"
                    data-testid={`revoke-code-${c.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Pager page={drillPage} total={drillTotal} pageSize={DRILL_PAGE_SIZE} itemLabel="codes" onChange={(p) => openBatch(drillBatch, p)} testId="drill-codes" />
          <DialogFooter>
            <Button
              onClick={() => downloadBatchCsv(drillBatch)}
              className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
            ><Download className="h-4 w-4 mr-2" /> Download CSV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
